import React from "react";
import PropTypes from "prop-types";

import JWPlayerAdapter from "./Adapters/JWPlayer";

export const PlayerContext = React.createContext();
PlayerContext.displayName = "PlayerContext";

export default function Player({
  children,
  unsafeOptions,
  onPlayerStart,
  onFullScreen,
  onPlay,
  onPause,
  onComplete,
  onError,
  onAdComplete,
  onAdStart,
  hideControls,
}) {
  const [playerAdapter, setPlayerAdapter] = React.useState();
  const containerRef = React.useRef(null);
  const isInitializingRef = React.useRef(false);

  const { autostart = false, playlist, stretching, adTag } = unsafeOptions;

  React.useEffect(() => {
    let unsubscribe = () => {};
    if (typeof onFullScreen === "function" && playerAdapter) {
      unsubscribe = playerAdapter.onFullScreen(onFullScreen);
    }
    return () => {
      unsubscribe();
    };
  }, [playerAdapter, onFullScreen]);

  React.useEffect(() => {
    let unsubscribe = () => {};
    if (typeof onPlay === "function" && playerAdapter) {
      unsubscribe = playerAdapter.onPlay(onPlay);
    }
    return () => {
      unsubscribe();
    };
  }, [onPlay, playerAdapter]);

  React.useEffect(() => {
    let unsubscribe = () => {};
    if (typeof onPause === "function" && playerAdapter) {
      unsubscribe = playerAdapter.onPause(onPause);
    }
    return () => {
      unsubscribe();
    };
  }, [onPause, playerAdapter]);

  React.useEffect(() => {
    let unsubscribe = () => {};
    if (typeof onComplete === "function" && playerAdapter) {
      unsubscribe = playerAdapter.onComplete(onComplete);
    }
    return () => {
      unsubscribe();
    };
  }, [onComplete, playerAdapter]);

  React.useEffect(() => {
    let unsubscribe = () => {};
    if (typeof onPlayerStart === "function" && playerAdapter) {
      unsubscribe = playerAdapter.onFirstPlay(onPlayerStart);
    }
    return () => {
      unsubscribe();
    };
  }, [onPlayerStart, playerAdapter]);

  React.useEffect(() => {
    let unsubscribe = () => {};
    if (typeof onAdComplete === "function" && playerAdapter) {
      unsubscribe = playerAdapter.onAdComplete(onAdComplete);
    }

    return () => {
      unsubscribe();
    };
  }, [onAdComplete, playerAdapter]);

  React.useEffect(() => {
    let unsubscribe = () => {};
    if (typeof onAdStart === "function" && playerAdapter) {
      unsubscribe = playerAdapter.onAdStart(onAdStart);
    }

    return () => {
      unsubscribe();
    };
  }, [onAdStart, playerAdapter]);

  React.useEffect(() => {
    if (playerAdapter) {
      playerAdapter.setAutostart(autostart);
    }
  }, [autostart, playerAdapter]);

  React.useEffect(() => {
    if (playerAdapter) {
      playerAdapter.setStretching(stretching);
    }
  }, [stretching, playerAdapter]);

  React.useEffect(() => {
    let unsubscribe = () => {};
    if (playerAdapter) {
      unsubscribe = playerAdapter.onError(onError);
    }

    return () => {
      unsubscribe();
    };
  }, [onError, playerAdapter]);

  React.useEffect(() => {
    if (playerAdapter) {
      playerAdapter.once("remove", () => {
        // We need to set ref to true so that we don't try to initialize the player again
        isInitializingRef.current = true;
        setPlayerAdapter(undefined);
      });
    }
  }, [playerAdapter]);

  const initializePlayer = React.useCallback(
    async (source) => {
      try {
        if (!playerAdapter) {
          const adapter = new JWPlayerAdapter(containerRef.current, {
            adTag,
            source,
            playlist,
          });
          await adapter.setup();
          setPlayerAdapter(adapter);
        }
      } catch (e) {
        onError(e);
      }
    },
    [adTag, onError, playerAdapter, playlist],
  );

  const load = React.useCallback(
    async (source) => {
      if (playerAdapter) {
        playerAdapter.load(source);
      } else if (!playerAdapter && isInitializingRef.current === false) {
        isInitializingRef.current = true;
        await initializePlayer(source);
        isInitializingRef.current = false;
      }
    },
    [initializePlayer, playerAdapter],
  );

  const remove = React.useCallback(() => {
    if (playerAdapter) {
      playerAdapter.remove();
    }
  }, [playerAdapter]);

  const value = React.useMemo(
    () => ({
      ...playerAdapter,
      isReady: !!playerAdapter,
      load,
      containerRef,
      hideControls,
      remove,
    }),
    [playerAdapter, load, containerRef, hideControls, remove],
  );

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  );
}

Player.propTypes = {
  unsafeOptions: PropTypes.shape({
    autostart: PropTypes.bool,
    mute: PropTypes.bool,
    playlist: PropTypes.string,
    adTag: PropTypes.string,
    stretching: PropTypes.oneOf(["uniform", "exactfit", "fill", "none"]),
  }),
  onPlayerStart: PropTypes.func,
  onFullScreen: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  onAdComplete: PropTypes.func,
  onAdStart: PropTypes.func,
  hideControls: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

Player.defaultProps = {
  unsafeOptions: {},
  onError: () => {},
  onPlayerStart: undefined,
  onFullScreen: undefined,
  onPlay: undefined,
  onPause: undefined,
  onComplete: undefined,
  onAdComplete: undefined,
  onAdStart: undefined,
  hideControls: false,
  children: null,
};
